import { arktypeResolver } from '@hookform/resolvers/arktype';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import { endOfDay, isAfter, isBefore, parseISO, startOfDay } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import React from 'react';

import { DatePicker } from '@/components/atoms/DatePicker';
import { ControllerWithError, ControllerWithErrorParam } from '@/forms/ControllerWithError';
import { TimezoneContext } from '@/hooks/useDateFormatter';
import { useFormWithErrors } from '@/hooks/useFormWithErrors';
import { useTranslator } from '@/hooks/useTranslator';
import {
  BeneficiariesFiltersFormData,
  beneficiariesFiltersSchema,
} from '@/schemas/beneficiariesFiltersSchema';
import { BeneficiaryType } from '@/types/customer';
import { GetBeneficiariesFilters } from '@/types/getBeneficiariesFilters';
import { DATE_DISPLAY_FORMAT, ISO_DATE_TIME_FORMAT } from '@/utility/dateFormats';

interface Props {
  readonly filters: GetBeneficiariesFilters;
  onChange(filter: GetBeneficiariesFilters): void;
}

const DEFAULT_FORM_VALUES: BeneficiariesFiltersFormData = {
  type: 'ALL',
  fromDate: '',
  toDate: '',
};

export const BeneficiariesFilters: React.FC<Props> = (props: Props): React.ReactElement => {
  const { filters, onChange } = props;
  const tr = useTranslator();
  const timezone = React.useContext(TimezoneContext);
  const fromDateResetRef = React.useRef<VoidFunction>((): void => {});
  const toDateResetRef = React.useRef<VoidFunction>((): void => {});

  const { control, handleSubmit, reset, formState, watch, trigger } =
    useFormWithErrors<BeneficiariesFiltersFormData>({
      defaultValues: {
        type: filters.type ?? 'ALL',
        fromDate: filters.fromDate ?? '',
        toDate: filters.toDate ?? '',
      },
      resolver: arktypeResolver(beneficiariesFiltersSchema),
      mode: 'onChange',
    });
  const fromDate = watch('fromDate');
  const toDate = watch('toDate');
  const { isDirty } = formState;

  const onSubmit = React.useCallback(
    (formData: BeneficiariesFiltersFormData): void => {
      onChange({
        ...filters,
        type: formData.type === 'ALL' ? undefined : formData.type,
        fromDate: formData.fromDate ?? undefined,
        toDate: formData.toDate ?? undefined,
      });
    },
    [onChange, filters],
  );

  const handleReset = React.useCallback((): void => {
    fromDateResetRef.current();
    toDateResetRef.current();

    reset(DEFAULT_FORM_VALUES);

    onChange({
      ...filters,
      type: undefined,
      fromDate: undefined,
      toDate: undefined,
    });
  }, [filters, onChange, reset]);

  return (
    <Stack component="form" onSubmit={handleSubmit(onSubmit)}>
      <Box sx={styles.container}>
        <ControllerWithError
          name="fromDate"
          label={tr('FROM_DATE', DATE_DISPLAY_FORMAT.toUpperCase())}
          control={control}
          render={({
            field,
          }: ControllerWithErrorParam<BeneficiariesFiltersFormData>): React.ReactElement => (
            <DatePicker
              {...field}
              label={tr('FROM_DATE', DATE_DISPLAY_FORMAT.toUpperCase())}
              value={field.value ? parseISO(field.value) : null}
              onChange={(date: Date | null): void => {
                if (!date) {
                  field.onChange('');
                } else {
                  const adjustedDate: Date = startOfDay(date);
                  const isoString: string = formatInTimeZone(
                    adjustedDate,
                    timezone,
                    ISO_DATE_TIME_FORMAT,
                  );
                  field.onChange(isoString);
                }
                trigger('toDate');
              }}
              format={DATE_DISPLAY_FORMAT}
              reset={fromDateResetRef}
              shouldDisableDate={(date: Date): boolean => isAfter(date, parseISO(toDate))}
            />
          )}
        />
        <ControllerWithError
          name="toDate"
          label={tr('TO_DATE', DATE_DISPLAY_FORMAT.toUpperCase())}
          control={control}
          render={({
            field,
          }: ControllerWithErrorParam<BeneficiariesFiltersFormData>): React.ReactElement => (
            <DatePicker
              {...field}
              label={tr('TO_DATE', DATE_DISPLAY_FORMAT.toUpperCase())}
              value={field.value ? parseISO(field.value) : null}
              onChange={(date: Date | null): void => {
                if (!date) {
                  field.onChange('');
                } else {
                  const adjustedDate: Date = endOfDay(date);
                  const isoString: string = formatInTimeZone(
                    adjustedDate,
                    timezone,
                    ISO_DATE_TIME_FORMAT,
                  );
                  field.onChange(isoString);
                }
              }}
              format={DATE_DISPLAY_FORMAT}
              reset={toDateResetRef}
              disableFuture
              shouldDisableDate={(date: Date): boolean => isBefore(date, parseISO(fromDate))}
            />
          )}
        />
        <ControllerWithError
          name="type"
          label={tr('TYPE')}
          control={control}
          render={({
            field,
          }: ControllerWithErrorParam<BeneficiariesFiltersFormData>): React.ReactElement => (
            <TextField {...field} select fullWidth>
              <MenuItem value="ALL">{tr('ALL')}</MenuItem>
              <MenuItem value={BeneficiaryType.individual}>{tr('INDIVIDUAL')}</MenuItem>
              <MenuItem value={BeneficiaryType.business}>{tr('BUSINESS')}</MenuItem>
            </TextField>
          )}
        />
      </Box>
      <Stack alignItems="center" mt={6} gap={3}>
        <Button type="submit" variant="contained" fullWidth disabled={!isDirty}>
          {tr('APPLY')}
        </Button>
        <Button variant="text" sx={styles.textButton} disableRipple onClick={handleReset}>
          {tr('RESET')}
        </Button>
      </Stack>
    </Stack>
  );
};

const styles: Record<string, SystemStyleObject> = {
  container: {
    display: 'grid',
    gap: 2,
    gridTemplateColumns: '1fr 1fr',
  },
  textButton: {
    color: 'text.primary',
  },
};
